/*--------------------------------------------------------------
 * Copyright (c) Microsoft Corporation. All rights reserved.
 * See License.txt in the project root for license information.
 *--------------------------------------------------------------*/

import * as MsDyn365 from '@msdyn365-commerce/core';
import { ICookieSetOptions, IImageDimension, ILocaleItems } from '@msdyn365-commerce/core-internal';
import { Button, getPayloadObject, getTelemetryAttributes, getTelemetryObject,
    IModuleProps, INodeProps, ITelemetryContent, Modal, ModalBody, ModalFooter, ModalHeader, onTelemetryClick } from '@msdyn365-commerce-modules/utilities';
import * as React from 'react';

import { ICountryPickerData } from './country-picker.data';
import { IActionLinkData, ICountryPickerProps } from './country-picker.props.autogenerated';

/**
 * CountryPicker view props interface.
 */
export interface ICountryPickerViewProps extends ICountryPickerProps<ICountryPickerData> {
    modalContainer: IModuleProps;
    modalHeaderContainer: INodeProps;
    modalFooterContainer: INodeProps;
    modalBodyContainer: INodeProps;
    heading?: React.ReactNode;
    subheading?: React.ReactNode;
    link?: React.ReactNode;
    countryList?: React.ReactNode[];
    isModalOpen?: boolean;
    state?: ICountryPickerState;
    stayOnCurrentSiteButton?: React.ReactNode;
}

/**
 * ICountryPickerState: Interface for Country picker State.
 */
export interface ICountryPickerState {
    isModalOpen: boolean;
}

/**
 *
 * CountryPicker component.
 * @extends {React.PureComponent<ICountryPickerProps<ICountryPickerData>>}
 */
export class CountryPicker extends React.PureComponent<ICountryPickerProps<ICountryPickerData>, ICountryPickerState> {

    private readonly _telemetryContent: ITelemetryContent = getTelemetryObject(this.props.context.request.telemetryPageName!,
        this.props.friendlyName,
        this.props.telemetry);

    private readonly _preferredSiteStorageKey: string = '_msdyn365___site_';

    private readonly _cookieExpirationTime = 5_184_000;

    public constructor(props: ICountryPickerProps<ICountryPickerData>) {
        super(props);
        let isCookieValueSet: boolean = false;
        if (props.context.request.cookies.get(this._preferredSiteStorageKey).value) {
            isCookieValueSet = true;
        }
        this.state = {
            isModalOpen: !isCookieValueSet && this.props.context.request.marketSettings !== undefined
        };
    }

    public render(): JSX.Element | null {
        const {
            heading,
            subheading,
            countryList,
            actionLink
        } = this.props.config;

        const isConsentGiven = this.props.context.request.cookies.isConsentGiven();

        if (!isConsentGiven) {
            return null;
        }

        const countryPickerHeading = heading && (
            <MsDyn365.Text
                className='ms-country-picker__heading'
                tag={heading.tag ?? 'h1'}
                text={heading.text}
                editProps={{ onEdit: this.handleTextChange, requestContext: this.props.context.request }}
            />
        );

        const countryPickerSubHeading = subheading && (
            <MsDyn365.RichTextComponent
                text={subheading}
                className='ms-country-picker__subheading'
                editProps={{ onEdit: this.handleParagraphChange, requestContext: this.props.context.request }}
            />
        );

        const countryPickerActionLink = actionLink && this.createLink(actionLink);
        const currentSiteButton = (<Button
            className='ms-country-picker__current-site' onClick={this._handleStayOnSiteButtonClick}>
            {this.props.resources.stayOnCurrentSiteButtonText}
        </Button>);

        const viewprops: ICountryPickerViewProps = {
            ...this.props,
            state: this.state,
            isModalOpen: this.state.isModalOpen,
            modalContainer: {
                moduleProps: this.props,
                tag: Modal,
                className: 'ms-country-picker__dialog',
                isOpen: true,
                'aria-label': 'country-picker',
                toggle: this.closeModal,
                modalTransition: { timeout: 0 },
                shouldUpdateFocusToFirstElement: true
            },
            modalHeaderContainer: {
                tag: ModalHeader,
                className: 'ms-country-picker__modal-header',
                toggle: this.closeModal
            },
            modalFooterContainer: {
                tag: ModalFooter,
                className: 'ms-countryPicker__modal-footer'
            },
            modalBodyContainer: {
                tag: ModalBody,
                className: 'ms-counrtyPicker__modal-body'
            },
            heading: countryPickerHeading,
            subheading: countryPickerSubHeading,
            link: countryPickerActionLink,
            countryList: countryList && this.renderCountryList(),
            stayOnCurrentSiteButton: currentSiteButton
        };

        return this.props.renderView(viewprops);
    }

    /**
     * Initiate close modal.
     */
    public readonly closeModal = (): void => {
        this.setState({
            isModalOpen: false
        });

    };

    public handleParagraphChange(event: MsDyn365.ContentEditableEvent): void {
        this.props.config.subheading = event.target.value;
    }

    public handleTextChange(event: MsDyn365.ContentEditableEvent): void {
        this.props.config.heading!.text = event.target.value;
    }

    public handleLinkTextChange(event: MsDyn365.ContentEditableEvent): void {
        if (this.props.config.actionLink?.linkText) {
            this.props.config.actionLink.linkText = event.target.value;
        }
    }

    public createLink(link?: IActionLinkData): React.ReactNode | null {
        if (link?.linkUrl) {
            const linkText = link.linkText ?? '';
            const payLoad = getPayloadObject('click', this._telemetryContent, '', '');
            const attributes = getTelemetryAttributes(this._telemetryContent, payLoad);
            const editableLink: MsDyn365.ILinksData = {
                ariaLabel: link.ariaLabel,
                className: 'ms-countryPicker__link',
                linkText,
                linkUrl: link.linkUrl.destinationUrl,
                openInNewTab: link.openInNewTab,
                role: 'link',
                additionalProperties: attributes!,
                onClick: onTelemetryClick(this._telemetryContent, payLoad, linkText)
            };
            return (
                <MsDyn365.Link
                    link={editableLink}
                    editProps={{ onTextChange: this.handleLinkTextChange, requestContext: this.props.context.request }}
                />
            );

        }
        return null;
    }

    public renderCountryList(): React.ReactNode[] | undefined {
        const marketSettings = this.props.context.request.marketSettings;
        if (!marketSettings) {
            return undefined;
        }
        const localeItems = marketSettings.localeItems;
        const countryLinks = localeItems.map((value: ILocaleItems) => {

            return (
                this.renderCountry(value)
            );
        });

        return countryLinks;

    }

    public renderCountry(localeItem: ILocaleItems): React.ReactNode | null {
        const countryList = this.props.config.countryList;
        let baseUrl = localeItem.localeBaseUrl;
        baseUrl = this._appendProtocolToUrl(baseUrl);
        const matchedCountryItem = countryList?.find(item => item.url === baseUrl);
        if (!matchedCountryItem) {
            return null;
        }
        const imageSize: IImageDimension = { q: 'w=48&h=48&m=6', w: 0, h: 0 };
        const defaultImageSettings: MsDyn365.IImageSettings = {
            viewports: {
                xs: imageSize,
                lg: imageSize,
                xl: imageSize
            },
            lazyload: true
        };
        const image = matchedCountryItem.image;
        const imageProps = {
            gridSettings: this.props.context.request.gridSettings!,
            imageSettings: image?.imageSettings ?? defaultImageSettings
        };
        return (
            <a
                key={matchedCountryItem.displayString}
                className='ms-country-picker__country__item__link'
                tabIndex={0}
                href={matchedCountryItem.url}
                aria-label={matchedCountryItem.displayString}
                onClick={this._handleClick(this._preferredSiteStorageKey, matchedCountryItem.url)}
            >
                {image && <MsDyn365.Image
                    requestContext={this.props.context.request}
                    className='ms-country-picker__country__item__img'
                    key={matchedCountryItem.displayString}
                    src={image.src ?? ''}
                    gridSettings={imageProps.gridSettings}
                    imageSettings={imageProps.imageSettings}
                    altText={image.altText}
                    loadFailureBehavior='hide'
                    shouldSkipToMainImage
                />}
                <div className='ms-country-picker__country__item__display'>
                    <span className='ms-country-picker__country__item__displayString'>
                        {matchedCountryItem.displayString}
                    </span>
                    <span className='ms-country-picker__country__item__displaySubString'>
                        {matchedCountryItem.displaySubString}
                    </span>
                </div>
            </a>
        );

    }

    /**
     * Writes the current site base url to cookie and closes the country picker modal dialog.
     */
    private readonly _handleStayOnSiteButtonClick = (): void => {
        const currentBaseUrl = this.props.context.request.marketSettings?.currentBaseUrl;
        if (currentBaseUrl) {
            const urlToStore = this._appendProtocolToUrl(currentBaseUrl);
            this._writeToCookie(this._preferredSiteStorageKey, urlToStore);
        }
        this.closeModal();
    };

    /**
     * Handles click event of country selection.
     * @param storageKey - Cookie storage key.
     * @param storageValue - Preffered site url.
     * @returns - This method just writes the cookie to the browser.
     */
    private readonly _handleClick = (storageKey?: string, storageValue?: string) => () => {
        this._writeToCookie(storageKey, storageValue);
    };

    /**
     * Handles click event of country selection.
     * @param storageKey - Cookie storage key.
     * @param storageValue - Preffered site url.
     */
    private readonly _writeToCookie = (storageKey?: string, storageValue?: string): void => {
        if (!storageKey || !storageValue) {
            return;
        }
        const cookieOptions: ICookieSetOptions = { secure: true, sameSite: 'none' };
        cookieOptions.maxAge = this._cookieExpirationTime;
        this.props.context.request.cookies.set(storageKey, storageValue, cookieOptions);
    };

    /**
     * Appends protocol to an url.
     * @param baseUrl - Url to be formatted.
     * @returns - URL after appending protocol.
     */
    private readonly _appendProtocolToUrl = (baseUrl: string): string => {
        return !baseUrl.includes('https://') ? `https://${baseUrl}` : baseUrl;
    };
}

export default CountryPicker;
